import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "./ui/dropdown-menu";
import { Bell, Check, CheckCheck, Trash2, CreditCard, Gift, Sparkles, Clock, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ICONS = {
  payment_approved: { Icon: CreditCard, color: "text-emerald-600", bg: "bg-emerald-50" },
  payment_rejected: { Icon: XCircle, color: "text-rose-600", bg: "bg-rose-50" },
  points_awarded: { Icon: Sparkles, color: "text-[#7C3AED]", bg: "bg-[#F5F3FF]" },
  redeem_requested: { Icon: Gift, color: "text-[#7C3AED]", bg: "bg-[#F5F3FF]" },
  redeem_claimed: { Icon: Gift, color: "text-emerald-600", bg: "bg-emerald-50" },
  subscription_expiring: { Icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
  subscription_expired: { Icon: Clock, color: "text-rose-600", bg: "bg-rose-50" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "ahora";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `hace ${Math.floor(diff / 86400)} d`;
  return d.toLocaleDateString();
}

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const pollRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [list, count] = await Promise.all([
        api.get("/notifications?limit=30"),
        api.get("/notifications/unread-count"),
      ]);
      setItems(list.data || []);
      setUnread(count.data?.count || 0);
    } catch (err) {
      // Polling fails silently on transient errors (auth refresh, network blip)
      console.debug("NotificationBell poll failed", err?.message);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchData();
    pollRef.current = setInterval(fetchData, 30000);
    return () => clearInterval(pollRef.current);
  }, [user, fetchData]);

  if (!user) return null;

  const handleClick = async (n) => {
    setOpen(false);
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`).catch((e) => console.debug("mark-read failed", e?.message));
      fetchData();
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch((e) => console.debug("mark-all failed", e?.message));
    fetchData();
  };

  const removeOne = async (e, id) => {
    e.stopPropagation();
    await api.delete(`/notifications/${id}`).catch((err) => console.debug("delete-notif failed", err?.message));
    fetchData();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative w-10 h-10 rounded-full hover:bg-[#F5F3FF] grid place-items-center transition-colors"
          data-testid="notification-bell"
          aria-label="Notificaciones"
        >
          <Bell className="w-5 h-5 text-zinc-700" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#7C3AED] text-white text-[10px] font-bold grid place-items-center"
              data-testid="notification-badge"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[380px] p-0 max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="font-display text-lg tracking-tight">Notificaciones</div>
          {items.length > 0 && (
            <button
              onClick={markAll}
              className="text-xs text-[#7C3AED] hover:underline inline-flex items-center gap-1"
              data-testid="notification-mark-all"
            >
              <CheckCheck className="w-3.5 h-3.5" /> Marcar todas
            </button>
          )}
        </div>
        <div className="max-h-[60vh] overflow-y-auto" data-testid="notification-list">
          {items.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-muted-foreground" data-testid="notification-empty">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-40" />
              No tienes notificaciones todavía.
            </div>
          ) : (
            items.map((n) => {
              const ic = ICONS[n.type] || { Icon: Bell, color: "text-zinc-600", bg: "bg-zinc-50" };
              const Ic = ic.Icon;
              return (
                <div
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`group w-full text-left px-4 py-3 border-b border-border hover:bg-[#FAFAFA] transition-colors flex gap-3 cursor-pointer ${n.read ? "" : "bg-[#F5F3FF]/40"}`}
                  data-testid={`notification-item-${n.type}`}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleClick(n); }}
                >
                  <div className={`w-9 h-9 rounded-lg ${ic.bg} grid place-items-center shrink-0`}>
                    <Ic className={`w-4 h-4 ${ic.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                      <div className={`text-sm leading-snug ${n.read ? "font-normal" : "font-semibold"}`}>{n.title}</div>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-[#7C3AED] mt-1.5 shrink-0" />}
                    </div>
                    {n.body && <div className="text-xs text-muted-foreground mt-1 leading-snug">{n.body}</div>}
                    <div className="text-[10px] text-zinc-400 mt-1.5">{timeAgo(n.created_at)}</div>
                  </div>
                  <span
                    onClick={(e) => removeOne(e, n.id)}
                    className="opacity-0 group-hover:opacity-100 self-start text-zinc-400 hover:text-rose-500 transition-opacity cursor-pointer"
                    role="button"
                    tabIndex={0}
                    aria-label="Eliminar"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </span>
                </div>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
